export const USER_FONT_STORAGE_KEY = 'zion_user_font'

export type ChatFontId =
  | 'system'
  | 'mono'
  | 'serif'
  | 'rounded'
  | 'condensed'
  | 'script'
  | 'cyber'
  | 'legible'
  | 'slab'
  | 'terminal'

export type ChatFontOption = {
  id: ChatFontId
  labelKey: string
  family: string
  sample: string
}

/** 10 font selezionabili per chat (sistema + Google Fonts). */
export const FONTS_CONFIG: readonly ChatFontOption[] = [
  {
    id: 'system',
    labelKey: 'settings_font_system',
    family: "Roboto, 'Roboto Flex', 'Noto Sans', 'Segoe UI', Helvetica, Arial, sans-serif",
    sample: 'Aa Bb 123',
  },
  {
    id: 'mono',
    labelKey: 'settings_font_mono',
    family: "'Fira Code', 'Courier New', Courier, monospace",
    sample: '{ cipher } 01',
  },
  {
    id: 'serif',
    labelKey: 'settings_font_serif',
    family: "Georgia, 'Times New Roman', Times, serif",
    sample: 'Elegante chat',
  },
  {
    id: 'rounded',
    labelKey: 'settings_font_rounded',
    family: "'Quicksand', 'Comfortaa', 'Trebuchet MS', sans-serif",
    sample: 'Rounded UI',
  },
  {
    id: 'condensed',
    labelKey: 'settings_font_condensed',
    family: "Impact, 'Arial Narrow', sans-serif-condensed, sans-serif",
    sample: 'COMPACT',
  },
  {
    id: 'script',
    labelKey: 'settings_font_script',
    family: "'Brush Script MT', 'Caveat', cursive",
    sample: 'Script flow',
  },
  {
    id: 'cyber',
    labelKey: 'settings_font_cyber',
    family: "'Orbitron', 'Rajdhani', sans-serif",
    sample: 'CYBER//ZION',
  },
  {
    id: 'legible',
    labelKey: 'settings_font_legible',
    family: "'Atkinson Hyperlegible', Arial, sans-serif",
    sample: 'Leggibilità',
  },
  {
    id: 'slab',
    labelKey: 'settings_font_slab',
    family: "'Rockwell', 'Courier Bold', serif",
    sample: 'Slab Serif',
  },
  {
    id: 'terminal',
    labelKey: 'settings_font_terminal',
    family: "'VT323', 'Consolas', monospace",
    sample: '> terminal_',
  },
] as const

const GOOGLE_FONTS_URL =
  'https://fonts.googleapis.com/css2?family=Atkinson+Hyperlegible&family=Caveat:wght@400;600&family=Comfortaa:wght@400;600&family=Fira+Code:wght@400;600&family=Orbitron:wght@500;700&family=Quicksand:wght@400;600&family=Rajdhani:wght@500;700&family=Roboto:wght@400;500;700&family=VT323&display=swap'

let fontsLinkInjected = false

function ensureGoogleFontsLoaded(): void {
  if (fontsLinkInjected || typeof document === 'undefined') return
  fontsLinkInjected = true
  const pre1 = document.createElement('link')
  pre1.rel = 'preconnect'
  pre1.href = 'https://fonts.googleapis.com'
  document.head.append(pre1)
  const pre2 = document.createElement('link')
  pre2.rel = 'preconnect'
  pre2.href = 'https://fonts.gstatic.com'
  pre2.crossOrigin = 'anonymous'
  document.head.append(pre2)
  const link = document.createElement('link')
  link.rel = 'stylesheet'
  link.href = GOOGLE_FONTS_URL
  document.head.append(link)
}

export function getUserChatFontId(): ChatFontId {
  try {
    const raw = localStorage.getItem(USER_FONT_STORAGE_KEY)?.trim()
    if (raw && FONTS_CONFIG.some((f) => f.id === raw)) return raw as ChatFontId
  } catch {
    /* ignore */
  }
  return 'system'
}

export function getUserChatFont(): ChatFontOption {
  const id = getUserChatFontId()
  return FONTS_CONFIG.find((f) => f.id === id) ?? FONTS_CONFIG[0]!
}

export function setUserChatFont(fontId: ChatFontId): void {
  if (!FONTS_CONFIG.some((f) => f.id === fontId)) return
  try {
    localStorage.setItem(USER_FONT_STORAGE_KEY, fontId)
  } catch {
    /* ignore */
  }
  applyUserChatFont()
}

export function applyUserChatFont(): void {
  const font = getUserChatFont()
  document.documentElement.style.setProperty('--user-chat-font', font.family)
  document.documentElement.dataset.chatFont = font.id
}

export function initUserChatFont(): void {
  ensureGoogleFontsLoaded()
  applyUserChatFont()
}
